import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'

interface PatientStats {
  total_patients: number
  active_patients: number
  new_patients_this_month: number
  by_gender: {
    male: number
    female: number
    other: number
  }
  by_age_group: {
    '0-18': number
    '19-40': number
    '41-60': number
    '61+': number
  }
}

interface SpecimenStats {
  total_specimens: number
  normal_specimens: number
  abnormal_specimens: number
  pending_specimens: number
  by_type: {
    tissue: number
    biopsy: number
    cytology: number
    fluid: number
  }
  abnormal_details: {
    mild: number
    moderate: number
    severe: number
    malignant: number
  }
}

interface DiagnosisStats {
  total_diagnoses: number
  by_status: {
    pending: number
    in_progress: number
    completed: number
  }
  by_result: {
    normal: number
    benign: number
    suspicious: number
    malignant: number
  }
  top_diagnoses: Array<{
    name: string
    count: number
  }>
}

const AnalyticsReports: React.FC = () => {
  const [patientStats, setPatientStats] = useState<PatientStats | null>(null)
  const [specimenStats, setSpecimenStats] = useState<SpecimenStats | null>(null)
  const [diagnosisStats, setDiagnosisStats] = useState<DiagnosisStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  })
  const [selectedFilter, setSelectedFilter] = useState({
    gender: 'all',
    age_group: 'all',
    specimen_type: 'all',
    result_type: 'all'
  })

  const fetchStatistics = async () => {
    try {
      setIsLoading(true)

      // Fetch patient statistics
      const patientRes = await fetch(`/api/v1/pathology/patients/statistics/?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`)
      if (patientRes.ok) {
        const patientData = await patientRes.json()
        setPatientStats(patientData)
      }

      // Fetch specimen statistics
      const specimenRes = await fetch(`/api/v1/pathology/specimens/statistics/?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`)
      if (specimenRes.ok) {
        const specimenData = await specimenRes.json()
        setSpecimenStats(specimenData)
      }

      // Fetch diagnosis statistics
      const diagnosisRes = await fetch(`/api/v1/diagnosis/statistics/?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`)
      if (diagnosisRes.ok) {
        const diagnosisData = await diagnosisRes.json()
        setDiagnosisStats(diagnosisData)
      }

    } catch (error) {
      console.error('Error fetching statistics:', error)
      toast.error('Không thể tải thống kê')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchStatistics()
  }, [dateRange])

  const exportToExcel = () => {
    toast.success('Đang xuất báo cáo...')
    // Implementation for export
  }

  const exportToPDF = () => {
    toast.success('Đang xuất PDF...')
    // Implementation for PDF export
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📊 Báo Cáo Thống Kê</h1>
          <p className="text-gray-600">Thống kê và phân tích bệnh nhân, mẫu bệnh phẩm</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={exportToExcel}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center"
          >
            📥 Xuất Excel
          </button>
          <button
            onClick={exportToPDF}
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 flex items-center"
          >
            📄 Xuất PDF
          </button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">🗓️ Lọc theo thời gian</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Từ ngày</label>
            <input
              type="date"
              value={dateRange.start_date}
              onChange={(e) => setDateRange({...dateRange, start_date: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Đến ngày</label>
            <input
              type="date"
              value={dateRange.end_date}
              onChange={(e) => setDateRange({...dateRange, end_date: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Giới tính</label>
            <select
              value={selectedFilter.gender}
              onChange={(e) => setSelectedFilter({...selectedFilter, gender: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tất cả</option>
              <option value="male">Nam</option>
              <option value="female">Nữ</option>
              <option value="other">Khác</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nhóm tuổi</label>
            <select
              value={selectedFilter.age_group}
              onChange={(e) => setSelectedFilter({...selectedFilter, age_group: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tất cả</option>
              <option value="0-18">0-18 tuổi</option>
              <option value="19-40">19-40 tuổi</option>
              <option value="41-60">41-60 tuổi</option>
              <option value="61+">61+ tuổi</option>
            </select>
          </div>
        </div>
      </div>

      {/* Patient Statistics */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">👥 Thống kê Bệnh nhân</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-sm text-blue-600 font-medium">Tổng số bệnh nhân</div>
            <div className="text-3xl font-bold text-blue-900 mt-2">
              {patientStats?.total_patients || 0}
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-sm text-green-600 font-medium">Đang điều trị</div>
            <div className="text-3xl font-bold text-green-900 mt-2">
              {patientStats?.active_patients || 0}
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="text-sm text-purple-600 font-medium">Bệnh nhân mới (tháng)</div>
            <div className="text-3xl font-bold text-purple-900 mt-2">
              {patientStats?.new_patients_this_month || 0}
            </div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-sm text-yellow-600 font-medium">Nam/Nữ</div>
            <div className="text-3xl font-bold text-yellow-900 mt-2">
              {patientStats?.by_gender.male || 0}/{patientStats?.by_gender.female || 0}
            </div>
          </div>
        </div>

        {/* Age Group Distribution */}
        <div className="mt-6">
          <h4 className="text-md font-semibold text-gray-800 mb-3">Phân bố theo độ tuổi</h4>
          <div className="grid grid-cols-4 gap-4">
            {patientStats && Object.entries(patientStats.by_age_group).map(([age, count]) => (
              <div key={age} className="bg-gray-50 p-3 rounded-lg text-center">
                <div className="text-sm text-gray-600">{age} tuổi</div>
                <div className="text-2xl font-bold text-gray-900">{count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Specimen Statistics */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">🧪 Thống kê Mẫu bệnh phẩm</h3>

        {/* Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-sm text-blue-600 font-medium">Tổng số mẫu</div>
            <div className="text-3xl font-bold text-blue-900 mt-2">
              {specimenStats?.total_specimens || 0}
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-sm text-green-600 font-medium">✅ Mẫu bình thường</div>
            <div className="text-3xl font-bold text-green-900 mt-2">
              {specimenStats?.normal_specimens || 0}
            </div>
            <div className="text-sm text-green-600 mt-1">
              ({specimenStats ? Math.round((specimenStats.normal_specimens / specimenStats.total_specimens) * 100) : 0}%)
            </div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-sm text-red-600 font-medium">⚠️ Mẫu bất thường</div>
            <div className="text-3xl font-bold text-red-900 mt-2">
              {specimenStats?.abnormal_specimens || 0}
            </div>
            <div className="text-sm text-red-600 mt-1">
              ({specimenStats ? Math.round((specimenStats.abnormal_specimens / specimenStats.total_specimens) * 100) : 0}%)
            </div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-sm text-yellow-600 font-medium">⏳ Đang chờ</div>
            <div className="text-3xl font-bold text-yellow-900 mt-2">
              {specimenStats?.pending_specimens || 0}
            </div>
          </div>
        </div>

        {/* Abnormal Details */}
        <div className="bg-red-50 p-4 rounded-lg mb-6">
          <h4 className="text-md font-semibold text-red-900 mb-3">📊 Chi tiết mẫu bất thường</h4>
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white p-3 rounded-lg text-center">
              <div className="text-sm text-gray-600">Nhẹ</div>
              <div className="text-2xl font-bold text-yellow-600">
                {specimenStats?.abnormal_details.mild || 0}
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg text-center">
              <div className="text-sm text-gray-600">Trung bình</div>
              <div className="text-2xl font-bold text-orange-600">
                {specimenStats?.abnormal_details.moderate || 0}
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg text-center">
              <div className="text-sm text-gray-600">Nặng</div>
              <div className="text-2xl font-bold text-red-600">
                {specimenStats?.abnormal_details.severe || 0}
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg text-center">
              <div className="text-sm text-gray-600">Ác tính</div>
              <div className="text-2xl font-bold text-red-900">
                {specimenStats?.abnormal_details.malignant || 0}
              </div>
            </div>
          </div>
        </div>

        {/* Specimen Types */}
        <div>
          <h4 className="text-md font-semibold text-gray-800 mb-3">Phân loại theo loại mẫu</h4>
          <div className="grid grid-cols-4 gap-4">
            {specimenStats && Object.entries(specimenStats.by_type).map(([type, count]) => (
              <div key={type} className="bg-gray-50 p-3 rounded-lg text-center">
                <div className="text-sm text-gray-600 capitalize">{type}</div>
                <div className="text-2xl font-bold text-gray-900">{count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Diagnosis Statistics */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">🔬 Thống kê Chẩn đoán</h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-sm text-blue-600 font-medium">Tổng chẩn đoán</div>
            <div className="text-3xl font-bold text-blue-900 mt-2">
              {diagnosisStats?.total_diagnoses || 0}
            </div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-sm text-yellow-600 font-medium">Đang xử lý</div>
            <div className="text-3xl font-bold text-yellow-900 mt-2">
              {diagnosisStats?.by_status.in_progress || 0}
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-sm text-green-600 font-medium">Hoàn thành</div>
            <div className="text-3xl font-bold text-green-900 mt-2">
              {diagnosisStats?.by_status.completed || 0}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600 font-medium">Chờ xử lý</div>
            <div className="text-3xl font-bold text-gray-900 mt-2">
              {diagnosisStats?.by_status.pending || 0}
            </div>
          </div>
        </div>

        {/* Diagnosis Results */}
        <div>
          <h4 className="text-md font-semibold text-gray-800 mb-3">Phân loại kết quả chẩn đoán</h4>
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-green-50 p-3 rounded-lg text-center">
              <div className="text-sm text-green-600">Bình thường</div>
              <div className="text-2xl font-bold text-green-900">
                {diagnosisStats?.by_result.normal || 0}
              </div>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg text-center">
              <div className="text-sm text-blue-600">Lành tính</div>
              <div className="text-2xl font-bold text-blue-900">
                {diagnosisStats?.by_result.benign || 0}
              </div>
            </div>
            <div className="bg-yellow-50 p-3 rounded-lg text-center">
              <div className="text-sm text-yellow-600">Nghi ngờ</div>
              <div className="text-2xl font-bold text-yellow-900">
                {diagnosisStats?.by_result.suspicious || 0}
              </div>
            </div>
            <div className="bg-red-50 p-3 rounded-lg text-center">
              <div className="text-sm text-red-600">Ác tính</div>
              <div className="text-2xl font-bold text-red-900">
                {diagnosisStats?.by_result.malignant || 0}
              </div>
            </div>
          </div>
        </div>

        {/* Top Diagnoses */}
        {diagnosisStats && diagnosisStats.top_diagnoses && diagnosisStats.top_diagnoses.length > 0 && (
          <div className="mt-6">
            <h4 className="text-md font-semibold text-gray-800 mb-3">Top 10 chẩn đoán thường gặp</h4>
            <div className="bg-gray-50 rounded-lg p-4">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 text-sm font-semibold text-gray-700">Chẩn đoán</th>
                    <th className="text-right py-2 text-sm font-semibold text-gray-700">Số lượng</th>
                    <th className="text-right py-2 text-sm font-semibold text-gray-700">Tỷ lệ</th>
                  </tr>
                </thead>
                <tbody>
                  {diagnosisStats.top_diagnoses.slice(0, 10).map((item, index) => (
                    <tr key={index} className="border-b last:border-0">
                      <td className="py-2 text-sm text-gray-900">{item.name}</td>
                      <td className="py-2 text-sm text-gray-900 text-right font-semibold">{item.count}</td>
                      <td className="py-2 text-sm text-gray-600 text-right">
                        {diagnosisStats ? Math.round((item.count / diagnosisStats.total_diagnoses) * 100) : 0}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Summary Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="text-2xl mr-3">ℹ️</div>
          <div>
            <h4 className="font-semibold text-blue-900">Ghi chú</h4>
            <p className="text-sm text-blue-800 mt-1">
              Dữ liệu thống kê được cập nhật theo thời gian thực.
              Sử dụng các bộ lọc trên để phân tích chi tiết theo nhóm đối tượng cụ thể.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AnalyticsReports
